import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionBadge } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import { LAUNCH_DATE, isLaunched } from "@/lib/launch";
import { useLang } from "@/lib/i18n";

const LAUNCHED = isLaunched();

function useCountdown() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, LAUNCH_DATE - now);
  return [
    Math.floor(diff / 86400000),
    Math.floor(diff / 3600000) % 24,
    Math.floor(diff / 60000) % 60,
    Math.floor(diff / 1000) % 60,
  ];
}

function ProjectCard() {
  const ref = useRef(null);
  const countdown = useCountdown();
  const { t } = useLang();
  const cdLabels = t("portfolio.countdownLabels");
  const tags = t("portfolio.tags");

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--spot-x", `${e.clientX - rect.left}px`);
    ref.current.style.setProperty("--spot-y", `${e.clientY - rect.top}px`);
  };

  return (
    <div
      ref={ref}
      data-testid="project-card-sendexpro"
      onMouseMove={onMove}
      style={{ "--spot-glow": "rgba(74, 108, 247, 0.18)" }}
      className="spotlight-card group relative h-[560px] md:h-[600px] rounded-2xl border border-[#262930] bg-[#121418] transition-colors duration-200 hover:border-[#4A6CF7]/60"
    >
      <div className="absolute inset-x-0 top-0 flex h-[42%] md:h-[55%] items-center justify-center bg-white">
        <img
          src="/sendexpro-logo.webp"
          alt="SendexPro logo"
          loading="lazy"
          className="h-auto w-[86%] max-w-xs object-contain transition-transform duration-500 group-hover:scale-[1.04] md:h-48 md:w-auto md:max-w-none"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/40 to-transparent" />

      <div className="relative z-[2] flex h-full flex-col justify-between p-5 md:p-7">
        <div className="flex items-start justify-between">
          {LAUNCHED ? (
            <motion.span
              data-testid="sendexpro-live-badge"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 14 }}
              className="flex items-center gap-2 rounded-full border border-[#4ADE80]/40 bg-[#0B0C0E]/70 px-3.5 py-1.5 font-tech text-[10px] uppercase tracking-[0.15em] text-[#4ADE80] backdrop-blur-sm"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4ADE80] opacity-60" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#4ADE80]" />
              </span>
              {t("portfolio.liveBadge")}
            </motion.span>
          ) : (
            <span
              data-testid="sendexpro-coming-soon-badge"
              className="flex items-center gap-2 rounded-full border border-[#4A6CF7]/40 bg-[#0B0C0E]/70 px-3.5 py-1.5 font-tech text-[10px] uppercase tracking-[0.15em] text-[#8FA6FF] backdrop-blur-sm"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4A6CF7] opacity-60" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#4A6CF7]" />
              </span>
              {t("portfolio.comingSoon")}<span className="hidden sm:inline">{t("portfolio.launchesIn")}</span>
            </span>
          )}
          <Link
            to="/ventures/sendexpro"
            data-testid="project-card-sendexpro-case-link"
            aria-label={t("portfolio.readCaseAria")}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#3A3F4B] bg-[#0B0C0E]/70 text-[#EAE6E1] backdrop-blur-sm transition-colors duration-200 hover:border-[#8C9A86] hover:text-[#8C9A86]"
          >
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
        <div>
          <h3 className="font-display text-2xl font-semibold tracking-tight text-[#EAE6E1]">
            SendexPro
          </h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-[#9E9E9E]">{t("portfolio.projectDesc")}</p>
          <div className="mt-3 md:mt-4 flex flex-nowrap gap-1.5 md:gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="whitespace-nowrap rounded-full border border-[#4A6CF7]/20 bg-[#181A20]/90 px-2 py-1 font-tech text-[7px] uppercase tracking-[0.08em] text-[#9EB0FF] md:px-3 md:text-[9px] md:tracking-[0.15em]"
              >
                {tag}
              </span>
            ))}
          </div>

          {LAUNCHED ? (
            <motion.div
              data-testid="sendexpro-launched-strip"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mt-5 flex flex-wrap items-center gap-4 border-t border-[#EAE6E1]/10 pt-4"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4ADE80] opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4ADE80]" />
              </span>
              <span className="font-display text-xl font-bold text-[#EAE6E1]">{t("portfolio.liveStripTitle")}</span>
              <span className="font-tech text-[10px] uppercase tracking-[0.15em] text-[#686D76]">
                {t("portfolio.liveStripNote")}
              </span>
            </motion.div>
          ) : (
            <div
              data-testid="sendexpro-launch-countdown"
              className="mt-4 md:mt-5 flex flex-nowrap items-center gap-x-3 md:gap-x-5 border-t border-[#EAE6E1]/10 pt-3 md:pt-4"
            >
              {countdown.map((value, i) => (
                <div key={cdLabels[i]} className="flex items-baseline gap-1.5">
                  <span className="font-tech text-base sm:text-2xl text-[#EAE6E1] tabular-nums">
                    {String(value).padStart(2, "0")}
                  </span>
                  <span className="font-tech text-[9px] md:text-[10px] uppercase tracking-[0.1em] md:tracking-[0.15em] text-[#686D76]">
                    {cdLabels[i]}
                  </span>
                </div>
              ))}
              <span className="hidden sm:inline font-tech text-[10px] uppercase tracking-[0.15em] text-[#F5C518]">
                {t("portfolio.untilLaunch")}
              </span>
            </div>
          )}

          <div className="mt-4 md:mt-5 flex flex-nowrap items-center gap-2.5 md:gap-3">
            <Link
              to="/ventures/sendexpro"
              data-testid="sendexpro-case-study-link"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#2B4BD8] px-4 py-2.5 text-[11px] font-semibold text-white transition-colors duration-200 hover:bg-[#4A6CF7] md:px-5 md:text-xs"
            >
              {t("portfolio.readCase")}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <a
              href="https://sendexpro.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sendexpro-live-site-link"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#3A3F4B] px-4 py-2.5 text-[11px] font-semibold text-[#EAE6E1] transition-colors duration-200 hover:border-[#F5C518] hover:text-[#F5C518] md:px-5 md:text-xs"
            >
              sendexpro.com
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlaceholderCard({ slot, mode }) {
  const { t } = useLang();
  const isInvite = mode === "invite";
  return (
    <div
      data-testid={`portfolio-placeholder-${slot}`}
      className="group flex h-60 md:h-72 flex-col items-center justify-center rounded-2xl border border-dashed border-[#3A3F4B] bg-[#121418]/50 p-3 md:p-7 text-center transition-colors duration-200 hover:border-[#8C9A86]"
    >
      <span className="font-tech text-[8px] md:text-[9px] uppercase tracking-[0.2em] text-[#C87A5B]">
        {t("portfolio.slotLabel")} {slot}
      </span>
      <h3 className="font-display mt-2 md:mt-3 text-base md:text-xl font-semibold tracking-tight text-[#EAE6E1]">
        {isInvite ? t("portfolio.haveIdea") : t("portfolio.comingSoonPh")}
      </h3>
      <span className="mt-2 font-tech text-[8px] md:text-[10px] uppercase tracking-[0.12em] md:tracking-[0.15em] leading-relaxed text-[#9E9E9E]">
        {isInvite ? t("portfolio.couldBeYou") : t("portfolio.inBuild")}
      </span>
      {isInvite && (
        <button
          data-testid={`placeholder-cta-${slot}`}
          onClick={() => scrollToId("contact")}
          className="mt-4 md:mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#8C9A86] px-5 py-3 text-[10px] font-semibold text-[#0B0C0E] transition-colors duration-200 hover:bg-[#9FAF98] md:px-6 md:py-3 md:text-xs"
        >
          {t("portfolio.claimSpot")}
          <ArrowUpRight className="h-3 w-3 md:h-3.5 md:w-3.5" />
        </button>
      )}
    </div>
  );
}

export default function Portfolio() {
  const { t } = useLang();
  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionBadge label={t("portfolio.badge")} testId="portfolio-badge" />
          <h2 className="font-display mt-5 max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug text-[#EAE6E1]">
            {t("portfolio.heading")}
          </h2>
          <p className="mt-4 max-w-xl text-base text-[#9E9E9E] leading-relaxed">
            {t("portfolio.sub")}
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-6">
          <Reveal className="col-span-2 md:col-span-12">
            <ProjectCard />
          </Reveal>
          {[
            { slot: "02", mode: "build" },
            { slot: "03", mode: "invite" },
          ].map((s, i) => (
            <Reveal key={s.slot} delay={0.08 * (i + 1)} className="col-span-1 md:col-span-6">
              <PlaceholderCard slot={s.slot} mode={s.mode} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
